/* eslint-disable react-refresh/only-export-components */
/**
 * Toast utility - Unified toast notification system
 *
 * Usage:
 *   import { showToast } from '@/components/Toast';
 *
 *   // Simple usage
 *   showToast('Message', 'success');
 *   showToast('Error occurred', 'error');
 *
 *   // With options
 *   showToast('Message', 'success', { title: 'Success!' });
 *   showToast('Details here', 'error', { title: 'Error', duration: 3000 });
 *
 *   // Backward compatible - duration as third argument
 *   showToast('Warning', 'warning', 3000);
 */
import { CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { toast as shadcnToast } from '@/hooks/ui/use-toast';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  title?: string;
  duration?: number;
}

const TOAST_ICON_SIZE = 20;

const toastIcons = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

const iconStyles = {
  success: 'text-green-500',
  error: 'text-red-500',
  info: 'text-blue-500',
  warning: 'text-yellow-500',
};

const titleStyles = {
  success: 'text-green-700',
  error: 'text-red-700',
  info: 'text-blue-700',
  warning: 'text-yellow-700',
};

/**
 * Show a toast notification
 * @param message - The message to display
 * @param type - Toast type: 'success' | 'error' | 'info' | 'warning'
 * @param options - Options object { title?, duration? } or duration number for backward compatibility
 */
export function showToast(
  message: string,
  type: ToastType = 'info',
  options?: ToastOptions | number
) {
  const Icon = toastIcons[type];

  // Handle backward compatibility: options can be a number (duration) or an object
  const opts: ToastOptions =
    typeof options === 'number' ? { duration: options } : options || {};
  const { title, duration = 5000 } = opts;

  // Map type to variant
  const variantMap = {
    success: 'success' as const,
    error: 'destructive' as const,
    warning: 'warning' as const,
    info: 'default' as const,
  };

  shadcnToast({
    title: title ? (
      <div className="flex items-center gap-2">
        <Icon
          size={TOAST_ICON_SIZE}
          className={`${iconStyles[type]} shrink-0`}
        />
        <h2 className={`${titleStyles[type]} font-medium`}>{title}</h2>
      </div>
    ) : undefined,
    description: title ? (
      <div className="whitespace-pre-line text-sm leading-relaxed ml-7">
        {message}
      </div>
    ) : (
      <div className="flex items-start gap-3">
        <Icon
          size={TOAST_ICON_SIZE}
          className={`${iconStyles[type]} shrink-0 mt-0.5`}
        />
        <span className="whitespace-pre-line text-sm leading-relaxed flex-1">
          {message}
        </span>
      </div>
    ),
    variant: variantMap[type],
    duration,
  });
}
